/**
 * Export Options Modal for the Historian Data Viewer (djin-cloud#806, task 18).
 *
 * Ported from genieos `web/src/components/historian/ExportOptionsModal.tsx`
 * (main checkout, not a worktree copy — 8854 bytes there). Kept: the data
 * summary, the row-count warnings, the `Modal` shell WITH a header (Ruling
 * AN — `gamma/Modal.tsx`'s `aria-labelledby="modal-title"` only resolves when
 * `chromeless` is false and a `title` is passed; do not use `chromeless`
 * here, and do not "fix" the modal itself from this PR).
 *
 * NOT ported: the delimiter / timestamp-format / include-units options.
 * Those were genieos's own `generateCSV` knobs; this repo's
 * `@/utils/chartExport.ts` (shared with `ProjectSummary.tsx` and
 * `AdminNotificationAudit.tsx` — Ruling AM) has a fixed, simpler contract
 * with none of them, and Ruling AM forbids touching that file from this PR.
 * Added instead: a metadata-header toggle (Ruling AM's `# Timezone:` line,
 * alongside the query id and range) and a "Copy query id" button.
 */

import { useState } from 'react';
import { Download, AlertTriangle, FileText, Copy, Check } from 'lucide-react';
import { useHistorian } from '@/contexts/HistorianContext';
import {
  buildHistorianExportRows,
  buildMetadataHeaderLines,
  downloadHistorianCsv,
  estimateExportSize,
  MAX_EXPORT_ROWS,
  WARN_EXPORT_ROWS,
} from '@/utils/historianExport';
import { cn } from '@/utils/cn';
import { Button, Modal } from '@/components/gamma';

export interface ExportOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExportComplete: (success: boolean, message: string) => void;
}

export const ExportOptionsModal: React.FC<ExportOptionsModalProps> = ({
  isOpen,
  onClose,
  onExportComplete,
}) => {
  const { state } = useHistorian();
  const { queryResult, selectedVariables, timeRange } = state;

  const [includeMetadataHeader, setIncludeMetadataHeader] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [copied, setCopied] = useState(false);

  const data = queryResult?.data ?? [];
  const rowCount = data.length;
  const columnCount = selectedVariables.length + 1; // +1 for timestamp
  const estimatedSize = estimateExportSize(rowCount, columnCount);
  const exceedsLimit = rowCount > MAX_EXPORT_ROWS;
  const showWarning = rowCount > WARN_EXPORT_ROWS;

  const handleExport = async () => {
    setIsExporting(true);

    // Small delay to show loading state.
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const { rows, headers } = buildHistorianExportRows(data, selectedVariables);
      const metadataLines = includeMetadataHeader
        ? buildMetadataHeaderLines({
            queryId: queryResult?.queryId ?? null,
            start: timeRange.start,
            end: timeRange.end,
          })
        : [];
      downloadHistorianCsv(rows, 'historian_export', headers, metadataLines);
      onExportComplete(true, `Exported ${rowCount.toLocaleString()} rows`);
      onClose();
    } catch (error) {
      onExportComplete(false, error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyQueryId = async () => {
    if (!queryResult?.queryId) return;
    try {
      await navigator.clipboard.writeText(queryResult.queryId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onExportComplete(true, 'Query ID copied to clipboard');
    } catch (error) {
      onExportComplete(false, error instanceof Error ? error.message : 'Failed to copy query ID');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-ems-blue" aria-hidden="true" />
          Export to CSV
        </div>
      }
      size="md"
    >
      <div className="space-y-4">
        {/* Data Summary */}
        <div className="bg-ems-bg-card-hover rounded-lg p-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-ems-text-secondary">Rows:</span>
            <span
              className={cn('font-mono', exceedsLimit ? 'text-ems-red' : 'text-ems-text-primary')}
            >
              {rowCount.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-ems-text-secondary">Columns:</span>
            <span className="text-ems-text-primary font-mono">{columnCount}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-ems-text-secondary">Est. Size:</span>
            <span className="text-ems-text-primary font-mono">{estimatedSize}</span>
          </div>
          {queryResult?.queryId && (
            <div className="flex justify-between items-center text-sm pt-1 border-t border-ems-border/50">
              <span className="text-ems-text-secondary">Query ID:</span>
              <div className="flex items-center gap-2">
                <span className="text-ems-text-primary font-mono text-xs truncate max-w-[160px]">
                  {queryResult.queryId}
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleCopyQueryId}
                  leftIcon={
                    copied ? (
                      <Check className="w-3.5 h-3.5 text-ems-green" aria-hidden="true" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                    )
                  }
                >
                  {copied ? 'Copied' : 'Copy query id'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Warnings */}
        {exceedsLimit && (
          <div
            className="flex items-start gap-2 p-3 bg-ems-red/10 border border-ems-red/30 rounded-lg"
            role="alert"
          >
            <AlertTriangle className="w-5 h-5 text-ems-red flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-ems-red font-medium text-sm">Export limit exceeded</p>
              <p className="text-ems-text-secondary text-xs mt-1">
                Client-side export is limited to {MAX_EXPORT_ROWS.toLocaleString()} rows. Please
                reduce the time range or increase the downsampling interval.
              </p>
            </div>
          </div>
        )}

        {showWarning && !exceedsLimit && (
          <div
            className="flex items-start gap-2 p-3 bg-ems-amber/10 border border-ems-amber/30 rounded-lg"
            role="alert"
          >
            <AlertTriangle
              className="w-5 h-5 text-ems-amber flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <p className="text-ems-amber font-medium text-sm">Large dataset</p>
              <p className="text-ems-text-secondary text-xs mt-1">
                Exporting {rowCount.toLocaleString()} rows may take a few seconds.
              </p>
            </div>
          </div>
        )}

        {/* Options */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-ems-text-primary">Options</h3>

          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={includeMetadataHeader}
              onChange={(e) => setIncludeMetadataHeader(e.target.checked)}
              className="w-4 h-4 rounded border border-ems-border bg-ems-blue focus:ring-ems-blue focus:ring-offset-0"
            />
            <span className="text-sm text-ems-text-secondary group-hover:text-ems-text-primary transition-colors">
              Include metadata header (query id, range, timezone)
            </span>
          </label>
          {/*
            Fix round 1 (task-18-review.md H2): this used to claim
            "Timestamps in the file are local wall-clock with no zone
            marker" — true of the OLD delegate-to-exportToCSV path, and
            false on THIS branch (the metadata one, which was always the
            hand-built ISO path) even before the round-1 fix unified both
            branches on ISO. Timestamps are ISO regardless of this
            checkbox now, so that line is stated unconditionally below and
            the checkbox-specific text describes only what it actually
            controls: the `#`-prefixed comment block.
          */}
          <p className="text-xs text-ems-text-secondary/70 pl-6">
            Timestamps are exported as ISO 8601 (UTC) either way.
          </p>
          {includeMetadataHeader && (
            <p className="text-xs text-ems-text-secondary/70 pl-6">
              The header comment records the query id, requested range, and the IANA timezone
              this export was generated in.
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-ems-border">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={exceedsLimit || isExporting || data.length === 0}
          loading={isExporting}
          loadingText="Exporting..."
          leftIcon={<Download className="w-4 h-4" />}
        >
          Export CSV
        </Button>
      </div>
    </Modal>
  );
};

export default ExportOptionsModal;
